import React, { useState, useEffect } from 'react';
import {
  Table, Card, Space, Button, Select, Spin, Typography, Row, Col,
  Statistic, Divider, Empty, Tag, message, Slider
} from 'antd';
import { DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import { getAllClasses } from '../../services/classService';
import { getClassAttendanceStats } from '../../services/attendanceService';
import { useAcademicYear } from '../../context/AcademicYearContext';

const { Title, Text } = Typography;

const ClassAttendanceReportPage = () => {
  const { currentYear } = useAcademicYear();
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [percentageRange, setPercentageRange] = useState([0, 100]);
  const [classAttendance, setClassAttendance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(15);
  const [stats, setStats] = useState({ avgAttendance: 0, totalPresent: 0 });

  useEffect(() => {
    const fetchClasses = async () => {
      setLoading(true);
      try {
        const data = await getAllClasses();
        setClasses(data);
      } catch (error) {
        message.error('Failed to load classes');
      } finally {
        setLoading(false);
      }
    };
    fetchClasses();
  }, [currentYear]);

  useEffect(() => {
    if (!selectedClass) {
      setClassAttendance([]);
      return;
    }

    const fetchAttendance = async () => {
      setLoading(true);
      try {
        // Build params object
        const params = {
          min_percentage: percentageRange[0],
          max_percentage: percentageRange[1]
        };

        const data = await getClassAttendanceStats(selectedClass, params);

        // Transform data for table
        const attendanceList = data.map(record => ({
          ...record,
          present: parseInt(record.present_count || 0, 10),
          absent: parseInt(record.absent_count || 0, 10),
          total: parseInt(record.total_days || 0, 10),
          percentage: parseFloat(record.percentage || 0).toFixed(2)
        }));

        setClassAttendance(attendanceList);

        // Calculate stats
        if (attendanceList.length > 0) {
          const totalAttendance = attendanceList.reduce((sum, s) => sum + s.present, 0);
          const totalDays = attendanceList.reduce((sum, s) => sum + s.total, 0);
          const avgAttendance = totalDays > 0 ? ((totalAttendance / totalDays) * 100).toFixed(2) : 0;
          setStats({ avgAttendance, totalPresent: totalAttendance });
        }
      } catch (error) {
        message.error('Failed to fetch attendance');
      } finally {
        setLoading(false);
      }
    };

    fetchAttendance();
  }, [selectedClass, percentageRange]);

  const columns = [
    {
      title: 'Admission No',
      dataIndex: 'admission_no',
      key: 'admission_no',
      width: 120,
    },
    {
      title: 'Student Name',
      dataIndex: 'student_name',
      key: 'student_name',
      filterSearch: true,
    },
    {
      title: 'Present',
      dataIndex: 'present',
      key: 'present',
      render: (present) => <Tag color="success">{present}</Tag>,
      width: 80,
    },
    {
      title: 'Absent',
      dataIndex: 'absent',
      key: 'absent',
      render: (absent) => <Tag color="error">{absent}</Tag>,
      width: 80,
    },
    {
      title: 'Total Days',
      dataIndex: 'total',
      key: 'total',
      width: 100,
    },
    {
      title: 'Percentage',
      dataIndex: 'percentage',
      key: 'percentage',
      render: (percent) => {
        const status = percent >= 75 ? 'success' : percent >= 50 ? 'warning' : 'error';
        return <Tag color={status}>{percent}%</Tag>;
      },
      width: 100,
    },
  ];

  const handleExport = () => {
    if (classAttendance.length === 0) {
      message.warning('No data to export');
      return;
    }

    const classData = classes.find(c => c.id === selectedClass);
    const csv = [
      [`Class Attendance Report - ${classData?.name || 'N/A'}`],
      [`Percentage Range: ${percentageRange[0]}% to ${percentageRange[1]}%`],
      [''],
      ['Admission No', 'Student Name', 'Present', 'Absent', 'Total Days', 'Percentage'],
      ...classAttendance.map(s => {
        return [s.admission_no, s.student_name, s.present, s.absent, s.total, s.percentage];
      })
    ]
      .map(row => row.join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `class_attendance_${classData?.name || 'report'}.csv`;
    a.click();
  };

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <Title level={2}>📊 Class-wise Attendance Report</Title>
          <Text type="secondary">
            View aggregate attendance report for an entire class filtered by attendance percentage
          </Text>
        </div>

        <Card>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={8}>
              <label>Select Class *</label>
              <Select
                placeholder="Choose a class"
                value={selectedClass}
                onChange={setSelectedClass}
                loading={loading}
                options={classes.map(c => ({
                  label: c.name,
                  value: c.id,
                }))}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={12} lg={16}>
              <label>Attendance Percentage Range ({percentageRange[0]}% - {percentageRange[1]}%)</label>
              <Slider
                range
                value={percentageRange}
                onChange={setPercentageRange}
                marks={{
                  0: '0%',
                  25: '25%',
                  50: '50%',
                  75: '75%',
                  100: '100%'
                }}
              />
            </Col>
          </Row>
        </Card>

        {classAttendance.length > 0 && (
          <>
            <Row gutter={16}>
              <Col xs={12} sm={8}>
                <Statistic
                  title="Class Average"
                  value={stats.avgAttendance}
                  suffix="%"
                  valueStyle={{ color: stats.avgAttendance >= 75 ? '#52c41a' : '#ff4d4f' }}
                />
              </Col>
              <Col xs={12} sm={8}>
                <Statistic
                  title="Total Present"
                  value={stats.totalPresent}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
            </Row>
            <Divider />
          </>
        )}

        {selectedClass ? (
          <Card
            title={`Attendance for ${classes.find(c => c.id === selectedClass)?.name || 'Selected Class'}`}
            extra={
              <Space>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleExport}
                  disabled={classAttendance.length === 0}
                >
                  Export CSV
                </Button>
                <Button icon={<PrinterOutlined />} onClick={() => window.print()}>
                  Print
                </Button>
              </Space>
            }
          >
            {loading ? (
              <Spin />
            ) : classAttendance.length > 0 ? (
              <Table
                columns={columns}
                dataSource={classAttendance.map((a, i) => ({ ...a, key: i }))}
                pagination={{
                  pageSize: pageSize,
                  showSizeChanger: true,
                  pageSizeOptions: [10, 15, 20, 50, 100],
                  onShowSizeChange: (current, size) => setPageSize(size),
                  onChange: (page, size) => setPageSize(size),
                  showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} students`
                }}
                scroll={{ x: 800 }}
              />
            ) : (
              <Empty description="No attendance records found within this range" />
            )}
          </Card>
        ) : (
          <Empty description="Please select a class to view attendance" />
        )}
      </Space>
    </div>
  );
};

export default ClassAttendanceReportPage;
